import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { formatKabulDisplay } from "./ScheduleFields";
import { FilterToolbar } from "./FilterToolbar";
import { LtrIsolate } from "./LtrIsolate";
import type { StatusTone } from "../lib/overview-ops";
import {
  BUNDLE_AUTHORITY_NOTE,
  jobRollbackRelationship,
  jobScopeLabel,
  jobStatusDisplay,
  matchFrozenRelease,
  relatedBundleLinks,
  templateFrozenBadge,
  templateStatusDisplay,
  truncateSha256,
  type BundleJobRow,
  type BundleTemplateRow,
  type JobSortKey,
  type TemplateSortKey,
} from "../lib/bundle-ops";

const TONE_BADGE: Record<StatusTone, string> = {
  success: "state-badge--published",
  warning: "state-badge--paused",
  danger: "state-badge--emergency",
  info: "state-badge--scheduled",
  neutral: "state-badge--archived",
};

async function copyText(text: string): Promise<void> {
  await navigator.clipboard.writeText(text);
}

function HashButton({ value, fullInTitle = true }: { value: string; fullInTitle?: boolean }) {
  return (
    <button
      type="button"
      className="btn btn--ghost btn--sm ltr-isolate"
      dir="ltr"
      title={fullInTitle ? value : undefined}
      aria-label={`کپی SHA-256: ${value}`}
      onClick={() => void copyText(value)}
    >
      <bdi dir="ltr" className="ltr-isolate latin-digits">
        {truncateSha256(value)}
      </bdi>
    </button>
  );
}

function FullHash({ value }: { value: string }) {
  return (
    <code
      className="content-inspect__mono content-inspect__mono--hash ltr-isolate"
      dir="ltr"
      title={value}
      aria-label={`SHA-256 کامل: ${value}`}
    >
      <bdi dir="ltr" className="ltr-isolate latin-digits">
        {value}
      </bdi>
    </code>
  );
}

/* ─── Template filters ─── */

type TemplateFilterProps = {
  status: string;
  onStatusChange: (value: string) => void;
  query: string;
  onQueryChange: (value: string) => void;
  sort: TemplateSortKey;
  onSortChange: (value: TemplateSortKey) => void;
};

export function BundleTemplateFilterBar({
  status,
  onStatusChange,
  query,
  onQueryChange,
  sort,
  onSortChange,
}: TemplateFilterProps) {
  const chips: Array<{ id: string; label: string }> = [
    { id: "all", label: "همه" },
    { id: "verified", label: "تأییدشده" },
    { id: "draft", label: "پیش‌نویس" },
    { id: "retired", label: "بازنشسته" },
    { id: "frozen", label: "منجمد" },
  ];
  return (
    <FilterToolbar
      className="list-filter-bar bundle-filter-bar announcements-filter-bar"
      aria-label="فیلتر الگوهای Bundle"
    >
      <div
        className="filter-toolbar__chips announcements-filter-bar__chips bundle-filter-bar__chips"
        role="group"
        aria-label="فیلتر وضعیت الگو"
      >
        {chips.map((c) => (
          <button
            key={c.id}
            type="button"
            className={`filter-chip${status === c.id ? " filter-chip--active" : ""}`}
            onClick={() => onStatusChange(c.id)}
            aria-pressed={status === c.id}
          >
            {c.label}
          </button>
        ))}
      </div>
      <div className="bundle-filter-bar__controls">
        <label className="announcements-filter-bar__field">
          <span className="visually-hidden">جستجوی الگو</span>
          <input
            type="search"
            value={query}
            onChange={(e) => onQueryChange(e.target.value)}
            placeholder="نسخه، SHA، نام فایل…"
            aria-label="جستجوی الگوی Bundle"
          />
        </label>
        <label className="announcements-filter-bar__field">
          <span>مرتب‌سازی</span>
          <select
            value={sort}
            onChange={(e) => onSortChange(e.target.value as TemplateSortKey)}
            aria-label="مرتب‌سازی الگوهای Bundle"
          >
            <option value="release">نسخه</option>
            <option value="status">وضعیت</option>
            <option value="created">ایجاد</option>
          </select>
        </label>
      </div>
    </FilterToolbar>
  );
}

/* ─── Job filters ─── */

type JobFilterProps = {
  status: string;
  onStatusChange: (value: string) => void;
  query: string;
  onQueryChange: (value: string) => void;
  sort: JobSortKey;
  onSortChange: (value: JobSortKey) => void;
  siteFilter: string;
  onSiteFilterChange: (value: string) => void;
  siteOptions: Array<{ id: string; code: string; name: string }>;
  onRefreshSiteFilter: () => void;
};

export function BundleJobFilterBar({
  status,
  onStatusChange,
  query,
  onQueryChange,
  sort,
  onSortChange,
  siteFilter,
  onSiteFilterChange,
  siteOptions,
  onRefreshSiteFilter,
}: JobFilterProps) {
  const chips: Array<{ id: string; label: string }> = [
    { id: "all", label: "همه" },
    { id: "ready", label: "آمادهٔ دانلود" },
    { id: "failed", label: "ناموفق" },
    { id: "revoked", label: "ابطال‌شده" },
    { id: "expired", label: "منقضی" },
  ];
  return (
    <FilterToolbar
      className="list-filter-bar bundle-filter-bar announcements-filter-bar"
      aria-label="فیلتر تاریخچه Bundle"
    >
      <div
        className="filter-toolbar__chips announcements-filter-bar__chips bundle-filter-bar__chips"
        role="group"
        aria-label="فیلتر وضعیت بسته"
      >
        {chips.map((c) => (
          <button
            key={c.id}
            type="button"
            className={`filter-chip${status === c.id ? " filter-chip--active" : ""}`}
            onClick={() => onStatusChange(c.id)}
            aria-pressed={status === c.id}
          >
            {c.label}
          </button>
        ))}
      </div>
      <div className="bundle-filter-bar__controls">
        <label className="announcements-filter-bar__field">
          <span>فیلتر سایت (API)</span>
          <select
            value={siteFilter}
            onChange={(e) => onSiteFilterChange(e.target.value)}
            aria-label="فیلتر سایت تاریخچه Bundle"
          >
            <option value="">همه</option>
            {siteOptions.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name} ({s.code})
              </option>
            ))}
          </select>
        </label>
        <button type="button" className="btn btn--ghost btn--sm" onClick={onRefreshSiteFilter}>
          به‌روزرسانی
        </button>
        <label className="announcements-filter-bar__field">
          <span className="visually-hidden">جستجوی بسته</span>
          <input
            type="search"
            value={query}
            onChange={(e) => onQueryChange(e.target.value)}
            placeholder="SITE_CODE، نسخه، SHA…"
            aria-label="جستجوی تاریخچه Bundle"
          />
        </label>
        <label className="announcements-filter-bar__field">
          <span>مرتب‌سازی</span>
          <select
            value={sort}
            onChange={(e) => onSortChange(e.target.value as JobSortKey)}
            aria-label="مرتب‌سازی تاریخچه Bundle"
          >
            <option value="requested">زمان درخواست</option>
            <option value="sequence">شماره</option>
            <option value="site">سایت</option>
            <option value="status">وضعیت</option>
          </select>
        </label>
      </div>
    </FilterToolbar>
  );
}

/* ─── Template inspect ─── */

type TemplateInspectProps = {
  item: BundleTemplateRow;
  actions?: ReactNode;
};

export function BundleTemplateInspectSummary({ item, actions }: TemplateInspectProps) {
  const status = templateStatusDisplay(item.status);
  const frozen = templateFrozenBadge(item.releaseId, item.sha256);
  const links = relatedBundleLinks();
  const frozenMatch = matchFrozenRelease(item.releaseId, item.sha256);
  // Authority copy lives on the page ops-safety-summary; keep identifier for D4F wiring.
  void BUNDLE_AUTHORITY_NOTE;
  const showFrozenMismatch = frozen?.tone === "danger" && Boolean(frozen.detail);
  const frozenMatchLabel =
    frozenMatch?.shaMatches === true
      ? "هم‌خوان"
      : frozenMatch?.shaMatches === false
        ? "ناهم‌خوان"
        : "بررسی‌نشده";

  return (
    <section
      className="content-inspect content-inspect--bundle"
      aria-labelledby="bundle-template-inspect-title"
    >
      <header className="content-inspect__hero">
        <div className="content-inspect__hero-row">
          <h2 id="bundle-template-inspect-title" className="content-inspect__name">
            <bdi dir="ltr" className="ltr-isolate latin-digits">
              {item.releaseId}
            </bdi>
          </h2>
          <div className="content-inspect__badges" aria-label="وضعیت الگو">
            <span className={`state-badge ${TONE_BADGE[status.tone]}`}>{status.label}</span>
            {item.isDefault && item.status === "verified" ? (
              <span className="state-badge state-badge--published">پیش‌فرض</span>
            ) : null}
            {frozen ? (
              <span className={`state-badge ${TONE_BADGE[frozen.tone]}`}>{frozen.label}</span>
            ) : null}
          </div>
        </div>
        <p className="content-inspect__code">
          <span className="content-inspect__code-label">منبع</span>
          <bdi dir="ltr" className="ltr-isolate latin-digits">
            {item.sourceFilename}
          </bdi>
        </p>
        <p className="content-inspect__id">
          شناسه{" "}
          <bdi dir="ltr" className="ltr-isolate latin-digits">
            {item.id}
          </bdi>
        </p>
      </header>

      <div className="content-inspect__block">
        <h3 className="content-inspect__block-title">هویت و هش</h3>
        {showFrozenMismatch ? (
          <p className="content-inspect__eligibility-detail">{frozen!.detail}</p>
        ) : null}
        <dl className="content-inspect__facts content-inspect__facts--stacked">
          <div className="content-inspect__fact--wide">
            <dt>SHA-256 (کامل)</dt>
            <dd>
              <FullHash value={item.sha256} />
            </dd>
          </div>
          {frozenMatch ? (
            <div>
              <dt>مرجع منجمد ({frozenMatch.label})</dt>
              <dd>
                {frozenMatchLabel}
                {frozenMatch.shaMatches === false ? (
                  <>
                    {" · "}
                    <FullHash value={frozenMatch.sha256} />
                  </>
                ) : null}
              </dd>
            </div>
          ) : null}
          <div>
            <dt>تعداد فایل</dt>
            <dd>
              <bdi dir="ltr" className="ltr-isolate latin-digits">
                {item.fileCount}
              </bdi>
            </dd>
          </div>
        </dl>
      </div>

      <div className="content-inspect__block content-inspect__block--audit">
        <h3 className="content-inspect__block-title">چرخه عمر</h3>
        <dl className="content-inspect__facts content-inspect__facts--pair">
          <div>
            <dt>ایجاد</dt>
            <dd>
              <bdi dir="ltr" className="ltr-isolate latin-digits">
                {formatKabulDisplay(item.createdAt)}
              </bdi>
            </dd>
          </div>
          <div>
            <dt>تأیید</dt>
            <dd>
              <bdi dir="ltr" className="ltr-isolate latin-digits">
                {formatKabulDisplay(item.verifiedAt)}
              </bdi>
            </dd>
          </div>
          {item.notes ? (
            <div className="content-inspect__fact--wide">
              <dt>یادداشت</dt>
              <dd>{item.notes}</dd>
            </div>
          ) : null}
        </dl>
      </div>

      <nav className="content-inspect__text-nav" aria-label="مسیرهای مرتبط انتشارها">
        {links.map((link) => (
          <Link key={link.id} to={link.to} className="content-inspect__text-link">
            {link.label}
          </Link>
        ))}
      </nav>

      {actions && <div className="content-inspect__actions">{actions}</div>}
    </section>
  );
}

/* ─── Job inspect ─── */

type JobInspectProps = {
  item: BundleJobRow;
  actions?: ReactNode;
};

export function BundleJobInspectSummary({ item, actions }: JobInspectProps) {
  const status = jobStatusDisplay(item.status);
  const frozen = templateFrozenBadge(item.templateReleaseId, item.templateSha256);
  const rollback = jobRollbackRelationship();
  const links = relatedBundleLinks();

  return (
    <section
      className="content-inspect content-inspect--bundle"
      aria-labelledby="bundle-job-inspect-title"
    >
      <header className="content-inspect__hero">
        <div className="content-inspect__hero-row">
          <h2 id="bundle-job-inspect-title" className="content-inspect__name">
            <bdi dir="ltr" className="ltr-isolate latin-digits">
              {item.siteCode}
            </bdi>
            {" · #"}
            <bdi dir="ltr" className="ltr-isolate latin-digits">
              {item.sequence}
            </bdi>
          </h2>
          <div className="content-inspect__badges" aria-label="وضعیت بسته">
            <span className={`state-badge ${TONE_BADGE[status.tone]}`}>{status.label}</span>
            {frozen ? (
              <span className={`state-badge ${TONE_BADGE[frozen.tone]}`}>{frozen.label}</span>
            ) : null}
          </div>
        </div>
        <p className="content-inspect__id">
          شناسه{" "}
          <bdi dir="ltr" className="ltr-isolate latin-digits">
            {item.id}
          </bdi>
        </p>
      </header>

      <div className="content-inspect__block">
        <h3 className="content-inspect__block-title">دامنه و الگو</h3>
        {status.detail ? (
          <p className="content-inspect__eligibility-detail">{status.detail}</p>
        ) : null}
        <dl className="content-inspect__facts content-inspect__facts--stacked">
          <div>
            <dt>دامنه / هدف</dt>
            <dd>
              {jobScopeLabel(item.siteCode)}
              {" · "}
              <bdi dir="ltr" className="ltr-isolate latin-digits">
                {item.siteCode}
              </bdi>
            </dd>
          </div>
          <div>
            <dt>نسخه الگو</dt>
            <dd>
              <bdi dir="ltr" className="ltr-isolate latin-digits">
                {item.templateReleaseId}
              </bdi>
            </dd>
          </div>
          <div className="content-inspect__fact--wide">
            <dt>SHA-256 الگو (کامل)</dt>
            <dd>
              <FullHash value={item.templateSha256} />
            </dd>
          </div>
          <div className="content-inspect__fact--wide">
            <dt>SHA-256 بسته (کامل)</dt>
            <dd>{item.artifactSha256 ? <FullHash value={item.artifactSha256} /> : "—"}</dd>
          </div>
          {item.artifactFilename ? (
            <div>
              <dt>نام فایل</dt>
              <dd>
                <bdi dir="ltr" className="ltr-isolate latin-digits">
                  {item.artifactFilename}
                </bdi>
              </dd>
            </div>
          ) : null}
          {item.artifactFileCount != null ? (
            <div>
              <dt>تعداد فایل بسته</dt>
              <dd>
                <bdi dir="ltr" className="ltr-isolate latin-digits">
                  {item.artifactFileCount}
                </bdi>
              </dd>
            </div>
          ) : null}
        </dl>
      </div>

      <div className="content-inspect__block content-inspect__block--audit">
        <h3 className="content-inspect__block-title">زمان‌بندی</h3>
        <dl className="content-inspect__facts content-inspect__facts--pair">
          <div>
            <dt>درخواست</dt>
            <dd>
              <bdi dir="ltr" className="ltr-isolate latin-digits">
                {formatKabulDisplay(item.requestedAt)}
              </bdi>
            </dd>
          </div>
          <div>
            <dt>اتمام</dt>
            <dd>
              <bdi dir="ltr" className="ltr-isolate latin-digits">
                {formatKabulDisplay(item.completedAt)}
              </bdi>
            </dd>
          </div>
          <div>
            <dt>انقضای دانلود</dt>
            <dd>
              <bdi dir="ltr" className="ltr-isolate latin-digits">
                {formatKabulDisplay(item.expiresAt)}
              </bdi>
            </dd>
          </div>
          {item.failureCode ? (
            <div>
              <dt>کد خطا</dt>
              <dd>
                <bdi dir="ltr" className="ltr-isolate latin-digits">
                  {item.failureCode}
                </bdi>
              </dd>
            </div>
          ) : null}
          <div className="content-inspect__fact--wide">
            <dt>رابطه بازگردانی</dt>
            <dd>
              {rollback.label}
              {rollback.detail ? (
                <span className="content-inspect__detail"> — {rollback.detail}</span>
              ) : null}
            </dd>
          </div>
        </dl>
      </div>

      <nav className="content-inspect__text-nav" aria-label="مسیرهای مرتبط انتشارها">
        {links.map((link) => (
          <Link key={link.id} to={link.to} className="content-inspect__text-link">
            {link.label}
          </Link>
        ))}
      </nav>

      {actions && <div className="content-inspect__actions">{actions}</div>}
    </section>
  );
}

/* ─── Template list ─── */

type TemplateListProps = {
  rows: BundleTemplateRow[];
  selectedId: string | null;
  onSelect: (id: string) => void;
  renderActions: (row: BundleTemplateRow) => ReactNode;
};

export function BundleTemplateInventoryList({
  rows,
  selectedId,
  onSelect,
  renderActions,
}: TemplateListProps) {
  return (
    <>
      <div
        className="sites-inventory-table-wrap inventory-table-wrap"
        role="region"
        aria-label="جدول الگوهای Bundle"
      >
        <table className="data-table content-inventory-table inventory-table">
          <thead>
            <tr>
              <th scope="col">نسخه</th>
              <th scope="col">SHA-256</th>
              <th scope="col">فایل‌ها</th>
              <th scope="col">وضعیت</th>
              <th scope="col">منجمد</th>
              <th scope="col">ایجاد</th>
              <th scope="col">
                <span className="visually-hidden">عملیات</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const selected = row.id === selectedId;
              const status = templateStatusDisplay(row.status);
              const frozen = templateFrozenBadge(row.releaseId, row.sha256);
              return (
                <tr
                  key={row.id}
                  className={selected ? "sites-inventory-table__row--selected" : undefined}
                >
                  <td>
                    <button
                      type="button"
                      className="sites-inventory__select-btn"
                      onClick={() => onSelect(row.id)}
                      aria-pressed={selected}
                    >
                      <LtrIsolate mono>{row.releaseId}</LtrIsolate>
                    </button>
                    {row.isDefault && row.status === "verified" && (
                      <div className="sites-inventory__muted">
                        <span className="state-badge state-badge--published">پیش‌فرض</span>
                      </div>
                    )}
                  </td>
                  <td>
                    <HashButton value={row.sha256} />
                  </td>
                  <td>
                    <LtrIsolate mono>{row.fileCount}</LtrIsolate>
                  </td>
                  <td>
                    <span className={`state-badge ${TONE_BADGE[status.tone]}`}>{status.label}</span>
                  </td>
                  <td>
                    {frozen ? (
                      <span className={`state-badge ${TONE_BADGE[frozen.tone]}`}>{frozen.label}</span>
                    ) : (
                      <span className="sites-inventory__muted">—</span>
                    )}
                  </td>
                  <td>
                    <div
                      className="inventory-cell-truncate"
                      title={formatKabulDisplay(row.createdAt)}
                    >
                      <LtrIsolate mono>{formatKabulDisplay(row.createdAt)}</LtrIsolate>
                    </div>
                  </td>
                  <td>
                    <div className="list-row__actions list-actions">
                      <button
                        type="button"
                        className="btn btn--ghost btn--sm"
                        onClick={() => onSelect(row.id)}
                      >
                        جزئیات
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <ul
        className="sites-inventory-cards content-inventory-cards inventory-cards"
        aria-label="کارت‌های الگوی Bundle"
      >
        {rows.map((row) => {
          const selected = row.id === selectedId;
          const status = templateStatusDisplay(row.status);
          const frozen = templateFrozenBadge(row.releaseId, row.sha256);
          return (
            <li
              key={row.id}
              className={`sites-inventory-card inventory-card${selected ? " sites-inventory-card--selected" : ""}`}
            >
              <button
                type="button"
                className="sites-inventory-card__hit"
                onClick={() => onSelect(row.id)}
                aria-pressed={selected}
              >
                <span className="sites-inventory-card__title">
                  <LtrIsolate mono>{row.releaseId}</LtrIsolate>
                </span>
                <span className="sites-inventory-card__badges">
                  <span className={`state-badge ${TONE_BADGE[status.tone]}`}>{status.label}</span>
                  {frozen && (
                    <span className={`state-badge ${TONE_BADGE[frozen.tone]}`}>{frozen.label}</span>
                  )}
                </span>
                <span className="sites-inventory__muted">
                  SHA:{" "}
                  <bdi dir="ltr" className="ltr-isolate latin-digits">
                    {truncateSha256(row.sha256)}
                  </bdi>
                </span>
                <span className="sites-inventory__muted">
                  فایل‌ها:{" "}
                  <bdi dir="ltr" className="ltr-isolate latin-digits">
                    {row.fileCount}
                  </bdi>
                  {" · ایجاد: "}
                  <bdi dir="ltr" className="ltr-isolate latin-digits">
                    {formatKabulDisplay(row.createdAt)}
                  </bdi>
                </span>
              </button>
              <div className="sites-inventory-card__actions">{renderActions(row)}</div>
            </li>
          );
        })}
      </ul>
    </>
  );
}

/* ─── Job list ─── */

type JobListProps = {
  rows: BundleJobRow[];
  selectedId: string | null;
  onSelect: (id: string) => void;
  renderActions: (row: BundleJobRow) => ReactNode;
};

export function BundleJobInventoryList({
  rows,
  selectedId,
  onSelect,
  renderActions,
}: JobListProps) {
  return (
    <>
      <div
        className="sites-inventory-table-wrap inventory-table-wrap"
        role="region"
        aria-label="جدول تاریخچه Bundle"
      >
        <table className="data-table content-inventory-table inventory-table">
          <thead>
            <tr>
              <th scope="col">#</th>
              <th scope="col">سایت</th>
              <th scope="col">نسخه پایه</th>
              <th scope="col">SHA بسته</th>
              <th scope="col">وضعیت</th>
              <th scope="col">منجمد الگو</th>
              <th scope="col">درخواست</th>
              <th scope="col">بازگردانی</th>
              <th scope="col">
                <span className="visually-hidden">عملیات</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const selected = row.id === selectedId;
              const status = jobStatusDisplay(row.status);
              const frozen = templateFrozenBadge(row.templateReleaseId, row.templateSha256);
              const rollback = jobRollbackRelationship();
              return (
                <tr
                  key={row.id}
                  className={selected ? "sites-inventory-table__row--selected" : undefined}
                >
                  <td>
                    <button
                      type="button"
                      className="sites-inventory__select-btn"
                      onClick={() => onSelect(row.id)}
                      aria-pressed={selected}
                    >
                      <LtrIsolate mono>{row.sequence}</LtrIsolate>
                    </button>
                  </td>
                  <td>
                    <div className="inventory-cell-truncate" title={row.siteCode}>
                      <LtrIsolate mono>{row.siteCode}</LtrIsolate>
                    </div>
                  </td>
                  <td>
                    <div className="inventory-cell-truncate" title={row.templateReleaseId}>
                      <LtrIsolate mono>{row.templateReleaseId}</LtrIsolate>
                    </div>
                  </td>
                  <td>
                    {row.artifactSha256 ? <HashButton value={row.artifactSha256} /> : "—"}
                  </td>
                  <td>
                    <span className={`state-badge ${TONE_BADGE[status.tone]}`}>{status.label}</span>
                    {row.failureCode && (
                      <div className="sites-inventory__muted inventory-cell-truncate" title={row.failureCode}>
                        <LtrIsolate mono>{truncateSha256(row.failureCode)}</LtrIsolate>
                      </div>
                    )}
                  </td>
                  <td>
                    {frozen ? (
                      <span className={`state-badge ${TONE_BADGE[frozen.tone]}`}>{frozen.label}</span>
                    ) : (
                      <span className="sites-inventory__muted">—</span>
                    )}
                  </td>
                  <td>
                    <div
                      className="inventory-cell-truncate"
                      title={formatKabulDisplay(row.requestedAt)}
                    >
                      <LtrIsolate mono>{formatKabulDisplay(row.requestedAt)}</LtrIsolate>
                    </div>
                  </td>
                  <td>
                    <span className={`state-badge ${TONE_BADGE[rollback.tone]}`}>
                      {rollback.label}
                    </span>
                  </td>
                  <td>
                    <div className="list-row__actions list-actions">
                      <button
                        type="button"
                        className="btn btn--ghost btn--sm"
                        onClick={() => onSelect(row.id)}
                      >
                        جزئیات
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <ul
        className="sites-inventory-cards content-inventory-cards inventory-cards"
        aria-label="کارت‌های تاریخچه Bundle"
      >
        {rows.map((row) => {
          const selected = row.id === selectedId;
          const status = jobStatusDisplay(row.status);
          const frozen = templateFrozenBadge(row.templateReleaseId, row.templateSha256);
          const rollback = jobRollbackRelationship();
          return (
            <li
              key={row.id}
              className={`sites-inventory-card inventory-card${selected ? " sites-inventory-card--selected" : ""}`}
            >
              <button
                type="button"
                className="sites-inventory-card__hit"
                onClick={() => onSelect(row.id)}
                aria-pressed={selected}
              >
                <span className="sites-inventory-card__title">
                  <bdi dir="ltr" className="ltr-isolate latin-digits">
                    {row.siteCode}
                  </bdi>
                  {" · #"}
                  <bdi dir="ltr" className="ltr-isolate latin-digits">
                    {row.sequence}
                  </bdi>
                </span>
                <span className="sites-inventory-card__badges">
                  <span className={`state-badge ${TONE_BADGE[status.tone]}`}>{status.label}</span>
                  {frozen && (
                    <span className={`state-badge ${TONE_BADGE[frozen.tone]}`}>{frozen.label}</span>
                  )}
                  <span className={`state-badge ${TONE_BADGE[rollback.tone]}`}>
                    {rollback.label}
                  </span>
                </span>
                <span className="sites-inventory__muted">
                  الگو:{" "}
                  <bdi dir="ltr" className="ltr-isolate latin-digits">
                    {row.templateReleaseId}
                  </bdi>
                </span>
                <span className="sites-inventory__muted">
                  SHA بسته:{" "}
                  <bdi dir="ltr" className="ltr-isolate latin-digits">
                    {row.artifactSha256 ? truncateSha256(row.artifactSha256) : "—"}
                  </bdi>
                </span>
                <span className="sites-inventory__muted">
                  درخواست:{" "}
                  <bdi dir="ltr" className="ltr-isolate latin-digits">
                    {formatKabulDisplay(row.requestedAt)}
                  </bdi>
                </span>
              </button>
              <div className="sites-inventory-card__actions">{renderActions(row)}</div>
            </li>
          );
        })}
      </ul>
    </>
  );
}
